import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  IdTokenClaims,
} from '@azure/msal-browser';
import { RedirectService, UserDataFacade } from '@boels/shared-state';
import { AppConfig, UserEnum } from '@boels/models';
import {
  monitorTokenExpiration,
  UserTrackingService,
  WindowRef,
  TokenExpirationAction,
  CookieService,
  REDIRECT_COOKIE_NAME,
} from '@boels/helpers';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class MsalAuthenticationService implements OnDestroy {
  private msalService = inject(MsalService);
  private userDataFacade = inject(UserDataFacade);
  private winRef = inject(WindowRef);
  private userTrackingService = inject(UserTrackingService);
  private cookieService = inject(CookieService);
  private redirectService = inject(RedirectService);
  private environment = inject<AppConfig>('ENVIRONMENT' as any);
  private document = inject<Document>(DOCUMENT);

  private readonly DESTROY$: Subject<void> = new Subject<void>();
  private readonly USER_LOGGED_IN$: Subject<number> = new Subject<number>();
  private readonly USER_LOGGED_OUT$: Subject<void> = new Subject<void>();
  private readonly TOKEN_REQUEST: { scopes: string[] };

  constructor() {
    this.TOKEN_REQUEST = {
      scopes: ['openid', this.environment.msalConfig.instance.auth.clientId],
    };
  }

  ngOnDestroy(): void {
    this.DESTROY$.next(undefined);
    this.DESTROY$.complete();
  }

  initializeMsal(): void {
    if (!this.winRef.isBrowser()) return;

    this.setObservers();
    this.handleRedirectObservable()
      .pipe(
        switchMap((hasRedirectResult: boolean) => {
          if (hasRedirectResult) return of(hasRedirectResult);
          return this.setOrRetrieveUserIfExists();
        }),
        catchError(() => {
          this.userDataFacade.clearUser();
          return of(true);
        }),
        takeUntil(this.DESTROY$),
      )
      .subscribe();
  }

  private setObservers(): void {
    this.observeLoginRequested().subscribe();
    this.observeLogoutRequested().subscribe();
    this.monitorTokenExpiration().subscribe();
  }

  private setOrRetrieveUserIfExists(): Observable<boolean | void> {
    const activeAccount = this.setActiveAccountIfExists();
    if (!activeAccount) {
      return this.tryAquireSsoTokenSilent();
    }
    return this.tryAquireSilentToken(activeAccount);
  }

  private observeLoginRequested(): Observable<void> {
    return this.userDataFacade.requestLoginDispatched$().pipe(
      tap((action) => {
        const redirectPath = action.redirectUrl
          ? action.redirectUrl
          : this.getFullLocation();
        this.setAuthorizeCookie(REDIRECT_COOKIE_NAME, redirectPath);

        this.redirectService.noCaching();
      }),
      switchMap(() =>
        this.msalService.loginRedirect({
          ...this.TOKEN_REQUEST,
          redirectUri: window.location.origin,
          domainHint: window.location.origin,
          authority: this.environment.msalConfig.instance.auth.authority,
          state: JSON.stringify({
            isManualLogin: true,
          }),
        }),
      ),
      takeUntil(this.DESTROY$),
    );
  }

  private observeLogoutRequested(): Observable<void> {
    return this.userDataFacade.requestLogoutDispatched$().pipe(
      switchMap((action) => {
        const redirectPath = action.redirectUrl
          ? window.location.origin + action.redirectUrl
          : this.getFullLocation();
        return this.logUserOut(redirectPath);
      }),
      takeUntil(this.DESTROY$),
    );
  }

  private logUserOut(redirectPath: string, account?: AccountInfo | null) {
    this.USER_LOGGED_OUT$.next();
    return this.msalService
      .logoutRedirect({
        ...this.TOKEN_REQUEST,
        account: account
          ? account
          : this.msalService.instance.getActiveAccount(),
        postLogoutRedirectUri: redirectPath,
      })
      .pipe(takeUntil(this.DESTROY$));
  }

  private handleRedirectObservable(): Observable<boolean> {
    return this.msalService.handleRedirectObservable().pipe(
      switchMap((result: AuthenticationResult | undefined) => {
        if (!result) return of(false);
        return this.handleAuthenticationResult(result);
      }),
      takeUntil(this.DESTROY$),
    );
  }

  private tryAquireSilentToken(account: AccountInfo | null) {
    return this.msalService
      .acquireTokenSilent({
        ...this.TOKEN_REQUEST,
        prompt: 'none',
        forceRefresh: true,
        account: account ? account : undefined,
      })
      .pipe(
        switchMap((result: AuthenticationResult) =>
          this.handleAuthenticationResult(result),
        ),
      );
  }

  private tryAquireSsoTokenSilent() {
    return this.msalService
      .ssoSilent({
        ...this.TOKEN_REQUEST,
        domainHint: window.location.origin,
        redirectUri: window.location.origin + '/assets/blank.html',
        authority: this.environment.msalConfig.instance.auth.authority,
      })
      .pipe(
        switchMap((result: AuthenticationResult) =>
          this.handleAuthenticationResult(result),
        ),
      );
  }

  private handleAuthenticationResult(
    result: AuthenticationResult,
  ): Observable<boolean> {
    this.msalService.instance.setActiveAccount(result.account);
    this.USER_LOGGED_IN$.next(this.convertExpireDateToUnixEpoch(result));

    const userId = (result.idTokenClaims as IdTokenClaims).sub;
    const isManualLogin = result.state
      ? JSON.parse(result.state as string).isManualLogin
      : false;
    if (userId && isManualLogin) {
      this.userTrackingService.setLoginEvent(userId);
    }

    return this.setUserData(result.accessToken, userId);
  }

  private setUserData(token: string, userId: string | undefined) {
    this.userDataFacade.setUserData({
      type: UserEnum.B2C_AUTHENTICATED,
      b2cToken: token,
      userId: userId,
    });
    return of(true);
  }

  private setActiveAccountIfExists(): AccountInfo | null {
    let activeAccount = this.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      activeAccount = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(activeAccount);
    }
    return activeAccount;
  }

  private getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  private setAuthorizeCookie(cookieName: string, value: string): void {
    this.cookieService.setCookie(cookieName, value);
  }

  private getFullLocation(): string {
    return window.location.pathname + window.location.search;
  }

  private convertExpireDateToUnixEpoch(result: AuthenticationResult): number {
    return (result.idTokenClaims as IdTokenClaims).exp || 0;
  }

  private monitorTokenExpiration(): Observable<void> {
    return monitorTokenExpiration(
      this.USER_LOGGED_IN$,
      this.USER_LOGGED_OUT$,
      this.document,
    ).pipe(
      switchMap((action: TokenExpirationAction) =>
        this.handleTokenExpiration(action),
      ),
      map(() => undefined),
      takeUntil(this.DESTROY$),
    );
  }

  private handleTokenExpiration(action: TokenExpirationAction) {
    const activeAccount = this.getActiveAccount() as AccountInfo;
    if (action === 'logout') {
      return of(this.localSessionLogout(activeAccount));
    }
    return this.tryAquireSilentToken(activeAccount);
  }

  private localSessionLogout(account: AccountInfo) {
    this.msalService
      .logoutRedirect({
        account: account,
        onRedirectNavigate: () => false,
      })
      .pipe(
        map(() => {
          this.winRef.removeAuthKeyPairsFromSession();
          this.userDataFacade.clearUser();
          return;
        }),
      )
      .subscribe();
  }
}
